import { useRef } from 'react';
import { CameraIcon } from '@heroicons/react/24/outline';

import { REPONSES_RAPIDES, type ReponseRapide } from '../../api/useMessagesMenage';
import BottomSheet from '../../components/BottomSheet';
import Button from '../../components/Button';
import Pills from '../../components/Pills';
import Text from '../../components/Text';
import { PICTOS_REPONSES } from '../../helpers/messagesMenage';
import { t } from '../../i18n/langue';

interface FeuilleOutilsMenageProps {
    onFermer: () => void;
    onReponseRapide: (cle: ReponseRapide) => void;
    onPhoto: (photo: File) => void;
    chambres: { id: number; name: string }[];
    chambreId: number | null;
    onChambre: (id: number | null) => void;
    /** Absent : pas de case « urgent » (réservée à la gouvernance, Q15). */
    urgent?: { valeur: boolean; onChange: (urgent: boolean) => void };
}

/**
 * Les outils du message, sous le « + » : réponses rapides à pictos (Q13), photo
 * (Q11), chambre concernée (Q12), urgent (Q15). Une réponse rapide ou une photo
 * part tout de suite : un seul geste pour qui ne lit pas.
 */
export default function FeuilleOutilsMenage({
    onFermer,
    onReponseRapide,
    onPhoto,
    chambres,
    chambreId,
    onChambre,
    urgent,
}: FeuilleOutilsMenageProps) {
    const refPhoto = useRef<HTMLInputElement>(null);
    const optionsChambres = [
        { valeur: 'aucune', libelle: t('messagesMenage.barre.aucuneChambre') },
        ...chambres.map((c) => ({ valeur: String(c.id), libelle: c.name })),
    ];

    return (
        <BottomSheet ouverte onClose={onFermer} libelle={t('messagesMenage.barre.reponsesRapides')} libelleFermeture={t('commun.fermer')}>
            <div className="flex flex-col gap-4">
                <Text variante="sousTitre" as="h2">
                    {t('messagesMenage.barre.reponsesRapides')}
                </Text>
                <div className="grid grid-cols-2 gap-2">
                    {REPONSES_RAPIDES.map((cle) => (
                        <Button
                            key={cle}
                            libelle={t(`messagesMenage.rapides.${cle}`)}
                            icone={PICTOS_REPONSES[cle]}
                            variante="secondaire"
                            alignement="debut"
                            pleineLargeur
                            onPress={() => onReponseRapide(cle)}
                        />
                    ))}
                </div>

                <Button
                    libelle={t('messagesMenage.barre.photo')}
                    icone={CameraIcon}
                    variante="secondaire"
                    pleineLargeur
                    onPress={() => refPhoto.current?.click()}
                />
                <input
                    ref={refPhoto}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    data-testid="photo-message"
                    onChange={(e) => {
                        const photo = e.target.files?.[0];
                        if (photo) onPhoto(photo);
                        e.target.value = '';
                    }}
                />

                {chambres.length > 0 && (
                    <Pills
                        libelle={t('messagesMenage.barre.chambre')}
                        options={optionsChambres}
                        valeur={chambreId === null ? 'aucune' : String(chambreId)}
                        onChange={(v) => onChambre(v === 'aucune' ? null : Number(v))}
                    />
                )}

                {urgent && (
                    <label className="flex min-h-touch items-center gap-3 rounded-xl bg-white px-3 py-2">
                        <input
                            type="checkbox"
                            checked={urgent.valeur}
                            onChange={(e) => urgent.onChange(e.target.checked)}
                            className="h-6 w-6 rounded border-border-color-light text-alert-text-color"
                        />
                        <Text as="span" ton="danger">
                            🔴 {t('messagesMenage.barre.urgent')}
                        </Text>
                    </label>
                )}
            </div>
        </BottomSheet>
    );
}
